import { MenuItemType } from '@components/Select';
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { apiClient, ApiResponse } from '@api/apiClient';
import { ErrorT } from '@api/types';
import { ToastType } from '@components/Toast';
import { SearchParams } from '@solidjs/router';
import { getCarrierPlaylists, setPlaylistsStore } from '@store/carriers';
import {
  carrierStore,
  Commodity,
  createPlaylist,
  EquipmentType,
  fetchCopyOrderDetails,
  fetchOrderDetails,
  getOrderTemplates,
  orderStore,
  Playlist,
  populateOrderWithQuote,
  populateOrderWithQuoteLegacy,
  setCommodityCodes,
  setEldAssetList,
  setEquipmentTypes,
} from '@store/orders';
import { userStore } from '@store/user';
import { handleToast } from '@utils/utils';

import {
  mapToComponent,
  mapToMessage,
  mapToTitle,
} from './components/ErrorsTable/constants';

export const getAndCheckPlaylists = async () => {
  const playlists = await getCarrierPlaylists();

  if (playlists !== undefined) {
    if (!playlists.some((p) => p.name === 'Favorites')) {
      const favorites: Playlist = {
        id: 0,
        userId: userStore.user.id,
        name: 'Favorites',
        carriers: [],
        groups: [],
        editable: false,
        canEdit: true,
        // @ts-expect-error This is how it's done in V3.
        user: null,
      };

      try {
        // If there's no favorites playlist for this user, create it.
        const favoritesPlaylist = await createPlaylist(favorites);

        if (favoritesPlaylist !== undefined) {
          playlists.push(favoritesPlaylist);
          setPlaylistsStore('playlists', [...playlists]);
        }
      } catch (error) {
        handleToast(ToastType.Error, (error as Error).message);
      }
    }
  }
};

export const getEldAssetList = async (carrierMC: string) => {
  try {
    const response = (await apiClient.get(
      `/tracking/eldassetlist?mc=${carrierMC}`,
    )) as unknown as ApiResponse<string[]>;

    if (!response.isSuccess) {
      return;
    }

    const menuList: MenuItemType[] = response.value
      .sort((a, b) => {
        return a.localeCompare(b, 'en', {
          numeric: true,
        });
      })
      .map((item) => ({
        label: item,
        value: item,
      }));

    setEldAssetList(menuList);
  } catch (error) {}
};

export const getCommodityCodes = async () => {
  try {
    const response: Commodity[] = await apiClient.get(
      '/LoadSure/GetCommodityCodes',
    );
    setCommodityCodes(response);
  } catch (error) {}
};

export const getEquipmentTypes = async () => {
  try {
    const response: EquipmentType[] = await apiClient.get(
      '/LoadSure/GetEquipmentTypes',
    );
    const sortedResponse = response.sort((a, b) =>
      a.name.localeCompare(b.name, 'en'),
    );
    setEquipmentTypes(sortedResponse);
  } catch (error) {}
};

export const checkErrorSection = () => {
  const newErrors = Object.entries(orderStore.orderFormErrorPerSection).reduce(
    (acc, v) => {
      const [key, hasError] = v;

      if (hasError === 0) {
        return acc;
      }

      acc.push({
        // @ts-expect-error This is how it's done in V3.
        title: mapToTitle[key],
        body: mapToMessage[key],
        onView: () => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const id = mapToComponent[key];

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (id !== undefined) {
            document.querySelector(id)?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'start',
            });
          }
        },
      });

      return acc;
    },
    [] as ErrorT[],
  );

  return newErrors;
};

export const isSafetyRequired = () => {
  return (
    Boolean(
      carrierStore.carrier[orderStore.activeTab.index]
        ?.requireMacropointTracking,
    ) ||
    Boolean(
      carrierStore.carrier[orderStore.activeTab.index]
        ?.shipperVerifyMCDotNumber,
    )
  );
};

export const setupOrder = async (
  query: Partial<SearchParams>,
  isCreate: boolean,
  orderIdUrl: string,
) => {
  await getOrderTemplates();

  // Allow setup to run only if we're not in create mode or if we have an LTL quote id.
  if (isCreate && !query.quoteId && !query.copyOrderId) {
    return;
  }

  // If there is a copy id
  if (query.copyOrderId != null) {
    await fetchCopyOrderDetails(query.copyOrderId as string);
  }

  if (!!query.ltlQuoteId && query.ltlQuoteId.length > 0) {
    await populateOrderWithQuoteLegacy(query.ltlQuoteId.toString());
  } else if (!!query.quoteId) {
    await populateOrderWithQuote(Number(query.quoteId));
  }

  // If there is an order id
  if (!isCreate) {
    await fetchOrderDetails(orderIdUrl, query.ltlQuoteId as string);
  }
};

export const handleError = (error: unknown) => {
  if (Array.isArray(error)) {
    error.forEach((err) => {
      handleToast(
        ToastType.Error,
        (err as { errorMessage: string }).errorMessage,
      );
    });
  } else {
    handleToast(ToastType.Error, (error as Error).message);
  }
};
